import React, { useCallback, useState, useEffect } from "react";
import { Form, Input, Row, Col, Select } from "antd";
import { useIntl } from "react-intl";

import AddressRow from "./addressRow";
import PersonalDepot from "./personalDepot";
import AddressSearch from "./addressSearch";
import {
  setZipCodePattern,
  setPhoneNumberPattern,
} from "../../../modules/consignmentFunctions";
import InfoLabel from "../../app/generic/infoLabel";
import InputItem from "../../app/generic/inputItem";
import CheckboxItem from "../../app/generic/checkboxItem";

import "./index.css";

const { Item } = Form;
const { Option } = Select;

function AddressForm({
  countriesForSelect,
  preselections,
  addressType,
  showSearch,
  countries,
  product,
  depots,
  form,
  detail,
  address,
  domestic,
  noColumns,
  loadDepots,
  onlyAddress,
  saveAsCustomer,
  formItemLayout,
  optionalContact,
  personalDepotId,
  onCountrySelect,
  disabledCountrySet,
  onSetChosenAddressId,
  onSetCountryCurrencies,
}) {
  const intl = useIntl();
  const { getFieldDecorator } = form;

  const [inputId] = useState(addressType || "");
  const [isPickup] = useState(addressType === "PC");
  const [isDelivery] = useState(addressType === "DL");

  countriesForSelect = countriesForSelect || countries;

  /**
   * spustí effect pouze jednou - bez hlídání změn závislostí
   * @param {Function} fun
   */
  const useMountEffect = (fun) => useEffect(fun, []);

  const [zipPattern, setZipPattern] = useState({});
  const [numberPattern, setNumberPattern] = useState({});
  const [selectedCountry, setSelectedCountry] = useState();
  const [canSetPreselection, setCanSetPreselection] = useState(true);
  const [selectedPreselection, setSelectedPreselection] = useState();
  const handlePreselectionSelect = useCallback(
    (preselectionId, fromCountry = false, validateForm = true) => {
      fromCountry !== true && setCanSetPreselection(false);
      setSelectedPreselection(
        preselections.find((c) => c.id === preselectionId)
      );
      const country = countries.find(
        (c) => c.preselectionId === preselectionId
      );
      country && setNumberPattern(setPhoneNumberPattern(country.code));
      validateForm &&
        setTimeout(() => form.validateFields([`contactPhone${inputId}`]), 500);
    },
    [form, inputId, countries, preselections]
  );

  const handleCountrySelect = useCallback(
    (value, setPreselection = true, validateForm = true, setForm = false) => {
      const country =
        countriesForSelect.find((c) => c.id === value) || countriesForSelect[0];
      setSelectedCountry(country);
      onCountrySelect && onCountrySelect(country);
      setZipPattern(setZipCodePattern(country.code));
      validateForm &&
        setTimeout(() => form.validateFields([`zipCode${inputId}`]), 500);
      if (setForm) {
        const fieldValue = {};
        fieldValue[`countryId${inputId}`] = country.id;
        form.setFieldsValue(fieldValue);
      }
      setPreselection &&
        canSetPreselection &&
        handlePreselectionSelect(country.preselectionId, true, validateForm);
      onSetCountryCurrencies && onSetCountryCurrencies(country.currencies);
    },
    [
      form,
      inputId,
      onCountrySelect,
      countriesForSelect,
      canSetPreselection,
      onSetCountryCurrencies,
      handlePreselectionSelect,
    ]
  );

  const handleChoosenAddress = useCallback(
    (address) => {
      onSetChosenAddressId && onSetChosenAddressId(address.id);
      handleCountrySelect(address.countryId, false, true, false);
    },
    [handleCountrySelect, onSetChosenAddressId]
  );

  useMountEffect(() => {
    if (
      (!product ||
        (product.code !== "ECI" &&
          product.code !== "SPI" &&
          product.code !== "ECI_B2C" &&
          product.code !== "ERI" &&
          product.code !== "PPI")) &&
      (!address || !address.countryId)
    ) {
      handleCountrySelect(
        (
          countriesForSelect.find(
            (c) =>
              c.code === "CZ" &&
              (isPickup ? c.forPickup : isDelivery ? c.forDelivery : true)
          ) ||
          countriesForSelect.find(
            (c) =>
              c.code !== "CZ" &&
              (isPickup ? c.forPickup : isDelivery ? c.forDelivery : true)
          )
        ).id,
        true,
        false
      );
    }

    if (address) {
      address.countryId && handleCountrySelect(address.countryId, false, true);
      address.preselectionId &&
        handlePreselectionSelect(address.preselectionId);
    }
  });

  const handleZipCodeChange = useCallback(
    (e) => {
      const value = e.target.value.toUpperCase();
      return selectedCountry &&
        (selectedCountry.code === "GB" || selectedCountry.code === "NL")
        ? value
        : value.replace(new RegExp(" ", "g"), "").trim();
    },
    [selectedCountry]
  );

  const handlePhoneChange = useCallback(
    (e) =>
      e.target.value
        .replace(new RegExp(" ", "g"), "")
        .replace(`${selectedPreselection && selectedPreselection.code}`, "")
        .replace(
          new RegExp(
            `^00${
              selectedPreselection && selectedPreselection.code.substring(1)
            }`
          ),
          ""
        ),
    [selectedPreselection]
  );

  const [countriesSubset, setCountriesSubset] = useState();
  useEffect(() => {
    (isPickup || isDelivery) &&
      countriesForSelect &&
      countriesForSelect !== "e" &&
      setCountriesSubset(
        countriesForSelect.filter((c) =>
          isPickup ? c.forPickup : c.forDelivery
        )
      );
  }, [countriesForSelect, isDelivery, isPickup]);

  useEffect(() => {
    countriesForSelect &&
      selectedCountry &&
      countriesForSelect.every((c) => c.id !== selectedCountry.id) &&
      handleCountrySelect(countriesForSelect[0].id, false, true, true);
  }, [countriesForSelect, selectedCountry, handleCountrySelect]);

  const [enabledPersonalDepot, setEnabledPersonalDepot] = useState(true);
  useEffect(() => {
    setEnabledPersonalDepot(
      (isDelivery && selectedCountry && selectedCountry.code !== "CZ") ||
        (product && (product.code === "ELI" || product.code === "ELD"))
        ? false
        : true
    );
  }, [product, isDelivery, selectedCountry]);

  return (
    <Form {...formItemLayout}>
      <Row>
        <Col>
          {showSearch && !detail ? (
            <AddressRow
              secondIcon
              noColumns={noColumns}
              firstChildren={
                <InputItem
                  maxLength={70}
                  label={intl.formatMessage({
                    id: "address.name",
                    defaultMessage: "Název",
                  })}
                  disabled={detail}
                  autoComplete="organization"
                  labelCol={!noColumns && 6}
                  wrapperCol={!noColumns && 17}
                  fieldName={`name${inputId}`}
                  formItemLayout={formItemLayout}
                  message={intl.formatMessage({
                    id: "address.emptyName",
                    defaultMessage: "Zadejte prosím název!",
                  })}
                  getFieldDecorator={getFieldDecorator}
                  initialValue={address && address.name}
                />
              }
              secondChildren={
                <AddressSearch
                  form={form}
                  domestic={domestic}
                  noColumns={noColumns}
                  addressType={addressType}
                  countries={countriesForSelect}
                  onChoosenAddress={handleChoosenAddress}
                />
              }
            />
          ) : (
            <InputItem
              label={intl.formatMessage({
                id: "address.name",
                defaultMessage: "Název",
              })}
              disabled={detail}
              autoComplete="organization"
              fieldName={`name${inputId}`}
              message={intl.formatMessage({
                id: "address.emptyName",
                defaultMessage: "Zadejte prosím název!",
              })}
              formItemLayout={formItemLayout}
              getFieldDecorator={getFieldDecorator}
              initialValue={address && address.name}
            />
          )}
          <InputItem
            label={intl.formatMessage({
              id: "address.subname",
              defaultMessage: "Název 2",
            })}
            disabled={detail}
            fieldName={`subname${inputId}`}
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            initialValue={address && address.subname}
          />
          <AddressRow
            noColumns={noColumns}
            firstChildren={
              <InputItem
                maxLength={40}
                label={intl.formatMessage({
                  id: "address.street",
                  defaultMessage: "Ulice",
                })}
                disabled={detail}
                labelCol={!noColumns && 9}
                wrapperCol={!noColumns && 15}
                autoComplete="street-address"
                fieldName={`street${inputId}`}
                formItemLayout={formItemLayout}
                message={intl.formatMessage({
                  id: "address.emptyStreet",
                  defaultMessage: "Zadejte prosím název ulice!",
                })}
                getFieldDecorator={getFieldDecorator}
                initialValue={address && address.street}
              />
            }
            secondChildren={
              <InputItem
                disabled={detail}
                labelCol={!noColumns && 14}
                wrapperCol={!noColumns && 10}
                label={intl.formatMessage({
                  id: "address.streetNumber",
                  defaultMessage: "Číslo popisné",
                })}
                formItemLayout={formItemLayout}
                fieldName={`streetNumber${inputId}`}
                message={intl.formatMessage({
                  id: "address.emptyStreetNumber",
                  defaultMessage: "Zadejte prosím číslo popisné!",
                })}
                getFieldDecorator={getFieldDecorator}
                initialValue={address && address.streetNumber}
              />
            }
          />
          <InputItem
            label={
              <InfoLabel
                info={intl.formatMessage({
                  id: "address.localDestinationInfo",
                  defaultMessage: "upřesnění adresy (číslo haly, areál, sklad)",
                })}
                label={intl.formatMessage({
                  id: "address.localDestination",
                  defaultMessage: "Místní určení",
                })}
              />
            }
            disabled={detail}
            autoComplete="address-level3"
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            fieldName={`localDestination${inputId}`}
            initialValue={address && address.localDestination}
          />
          <InputItem
            maxLength={40}
            label={intl.formatMessage({
              id: "address.city",
              defaultMessage: "Město",
            })}
            disabled={detail}
            fieldName={`city${inputId}`}
            autoComplete="address-level2"
            message={intl.formatMessage({
              id: "address.emptyCity",
              defaultMessage: "Zadejte prosím město!",
            })}
            formItemLayout={formItemLayout}
            getFieldDecorator={getFieldDecorator}
            initialValue={address && address.city}
          />
          <AddressRow
            secondBigger
            noColumns={noColumns}
            firstChildren={
              <Item
                label={intl.formatMessage({
                  id: "address.zipCode",
                  defaultMessage: "PSČ",
                })}
                labelCol={noColumns ? undefined : { span: 13 }}
                wrapperCol={noColumns ? undefined : { span: 11 }}
              >
                {getFieldDecorator(`zipCode${inputId}`, {
                  ...(address && { initialValue: address.zipCode }),
                  getValueFromEvent: handleZipCodeChange,
                  rules: [
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "address.emptyZipCode",
                        defaultMessage: "Zadejte prosím PSČ!",
                      }),
                    },
                    zipPattern,
                  ],
                })(
                  <Input
                    allowClear
                    disabled={detail}
                    autoComplete="postal-code"
                  />
                )}
              </Item>
            }
            secondChildren={
              <Item
                label={intl.formatMessage({
                  id: "address.country",
                  defaultMessage: "Stát",
                })}
                labelCol={noColumns ? undefined : { span: 8 }}
                wrapperCol={noColumns ? undefined : { span: 16 }}
              >
                {getFieldDecorator(`countryId${inputId}`, {
                  initialValue: selectedCountry && selectedCountry.id,
                  rules: [
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "address.emptyCountry",
                        defaultMessage: "Vyberte prosím stát!",
                      }),
                    },
                  ],
                })(
                  <Select
                    allowClear
                    showSearch
                    onChange={handleCountrySelect}
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    disabled={disabledCountrySet || detail}
                  >
                    {(countriesSubset || countriesForSelect).map((c, i) => (
                      <Option key={i} value={c.id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                )}
              </Item>
            }
          />
          <InputItem
            disabled={detail}
            label={intl.formatMessage({
              id: "address.contactPerson",
              defaultMessage: "Kontaktní osoba",
            })}
            optional={optionalContact}
            formItemLayout={formItemLayout}
            fieldName={`contactPerson${inputId}`}
            message={intl.formatMessage({
              id: "address.emptyContactPerson",
              defaultMessage: "Zadejte prosím kontaktní osobu!",
            })}
            getFieldDecorator={getFieldDecorator}
            initialValue={address && address.contactPerson}
          />
          <AddressRow
            equal
            noColumns={noColumns}
            firstChildren={
              <Item
                label={intl.formatMessage({
                  id: "address.contactPhone",
                  defaultMessage: "Telefon",
                })}
                labelCol={noColumns ? undefined : { span: 10 }}
                wrapperCol={noColumns ? undefined : { span: 14 }}
              >
                {getFieldDecorator(`contactPhone${inputId}`, {
                  ...(address && { initialValue: address.contactPhone }),
                  getValueFromEvent: handlePhoneChange,
                  rules: [
                    {
                      required: optionalContact ? false : true,
                      message: intl.formatMessage({
                        id: "address.emptyContactPhone",
                        defaultMessage: "Zadejte prosím telefonní kontakt!",
                      }),
                    },
                    numberPattern,
                  ],
                })(
                  <Input
                    allowClear
                    disabled={detail}
                    autoComplete="tel"
                    addonBefore={getFieldDecorator(`preselectionId${inputId}`, {
                      initialValue:
                        selectedPreselection && selectedPreselection.id,
                      rules: [
                        {
                          required: optionalContact ? false : true,
                          message: intl.formatMessage({
                            id: "address.emptyPreselection",
                            defaultMessage: "Vyberte prosím předvolbu!",
                          }),
                        },
                      ],
                    })(
                      <Select
                        showSearch
                        disabled={detail}
                        onChange={handlePreselectionSelect}
                        className="address-preselection-select"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {preselections.map((p, i) => (
                          <Option key={i} value={p.id}>
                            {p.code}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                )}
              </Item>
            }
            secondChildren={
              <InputItem
                multipleEmails
                label={
                  <InfoLabel
                    info={intl.formatMessage({
                      id: "address.contactEmail.info",
                      defaultMessage:
                        "Je možné zadat více emailových adres oddělených středníkem, v celkové délce 128 znaků.",
                    })}
                    label={intl.formatMessage({
                      id: "address.contactEmail",
                      defaultMessage: "Email",
                    })}
                  />
                }
                disabled={detail}
                autoComplete="email"
                labelCol={!noColumns && 7}
                wrapperCol={!noColumns && 17}
                formItemLayout={formItemLayout}
                fieldName={`contactEmail${inputId}`}
                getFieldDecorator={getFieldDecorator}
                initialValue={address && address.contactEmail}
              />
            }
          />
          {selectedCountry && selectedCountry.code === "GR" && (
            <InputItem
              optional
              disabled={detail}
              label={intl.formatMessage({
                id: "address.vat",
                defaultMessage: "VAT",
              })}
              formItemLayout={formItemLayout}
              fieldName={`vat${inputId}`}
              /*message={intl.formatMessage({
                id: "address.emptyVat",
                defaultMessage: "Zadejte prosím VAT!",
              })}*/
              getFieldDecorator={getFieldDecorator}
              initialValue={address && address.vat}
            />
          )}
          {!onlyAddress && enabledPersonalDepot && (
            <PersonalDepot
              form={form}
              depots={depots}
              detail={detail}
              product={product}
              inputId={inputId}
              loadDepots={loadDepots}
              addressType={addressType}
              personalDepotId={personalDepotId}
              selectedCountry={selectedCountry}
            />
          )}
          {saveAsCustomer && (
            <Row>
              <Col xxl={{ span: 5, offset: 4 }} sm={{ span: 6, offset: 5 }}>
                <CheckboxItem
                  wrapperCol={24}
                  disabled={detail}
                  formItemLayout={formItemLayout}
                  checkboxLabel={intl.formatMessage({
                    id: "address.saveAsCustomer",
                    defaultMessage: "Uložit jako zákazníka",
                  })}
                  fieldName={`customer${inputId}`}
                  getFieldDecorator={getFieldDecorator}
                  initialValue={address ? address.customer : isDelivery}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Form>
  );
}

export default Form.create()(AddressForm);
